import type { DrilldownConfig } from '../../../types/bi';

/** Data passed from a chart click event */
export interface DrilldownEvent {
  dimensionValue: string;
  dimensionField: string;
  measureValue?: number;
  seriesName?: string;
}

/** A drill-down filter currently active on the dashboard */
export interface DrilldownFilter {
  field_key: string;
  value: string;
  label: string;
}

/** The resolved action to take on a drill-down click */
export type DrilldownAction =
  | { type: 'filter'; filter: DrilldownFilter }
  | { type: 'navigate'; url: string };

/**
 * Resolves a chart click event into a drill-down action.
 *
 * - If drilldown_config.action is 'filter', returns a filter to add to the dashboard.
 * - If drilldown_config.action is 'navigate', returns a URL to navigate to.
 * - If no config is provided, defaults to 'filter' using the first dimension.
 */
export function resolveDrilldown(
  config: DrilldownConfig | null | undefined,
  event: DrilldownEvent,
): DrilldownAction | null {
  if (!event.dimensionValue && event.dimensionValue !== '0') return null;

  const action = config?.action || 'filter';
  const targetField = config?.target_field || event.dimensionField;

  if (action === 'navigate') {
    const baseUrl = config?.target_url || '/cases';
    const separator = baseUrl.includes('?') ? '&' : '?';
    const url = `${baseUrl}${separator}${encodeURIComponent(targetField)}=${encodeURIComponent(event.dimensionValue)}`;
    return { type: 'navigate', url };
  }

  // Default: filter action
  return {
    type: 'filter',
    filter: {
      field_key: targetField,
      value: event.dimensionValue,
      label: `${targetField}: ${event.dimensionValue}`,
    },
  };
}

/**
 * Builds an ECharts click handler that extracts the dimension value
 * from the chart click event and invokes the callback.
 */
export function buildChartClickHandler(
  dimensions: string[],
  onDrillDown?: (event: DrilldownEvent) => void,
) {
  if (!onDrillDown) return undefined;

  return {
    click: (params: {
      name?: string;
      seriesName?: string;
      value?: unknown;
      data?: Record<string, unknown>;
    }) => {
      const dimensionField = dimensions[0] || '';
      const dimensionValue = String(params.name ?? params.data?.[dimensionField] ?? '');
      if (!dimensionValue) return;

      onDrillDown({
        dimensionValue,
        dimensionField,
        measureValue: typeof params.value === 'number' ? params.value : undefined,
        seriesName: params.seriesName,
      });
    },
  };
}
